import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  Modal,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ModeEditOutlineRoundedIcon from '@mui/icons-material/ModeEditOutlineRounded';
import RedeemIcon from '@mui/icons-material/Redeem';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import {
  COLOR_GREEN,
  COLOR_PRIMARY,
  COLOR_RED,
  CountryCodeWithCurrency,
  CurrencyWithSubscriptionPrice,
  exchangeRates,
} from 'src/const';
import {
  COLOR_GREY_600,
  COLOR_GREY_800,
  COLOR_PRIMARY_1_100,
  COLOR_PRIMARY_2,
  COLOR_PRIMARY_2_100,
  COLOR_SUCCESS,
  COLOR_TEXT_1,
  COLOR_TEXT_2,
  COLOR_TEXT_3,
  COLOR_TEXT_4,
  COLOR_TEXT_GREY,
} from 'src/colors';

import GiftImage from 'src/App/assets/gift-svg.svg';
import { callApi } from 'src/core/callApi';
import { formatCurrency, formatDate } from 'src/utils/functions';
import { useSelector } from 'react-redux';
import { User } from 'src/interfaces/iuser';
import FullScreenLoader from 'src/App/Components/Loaders/FullScreenAnimationLoader';

const CancellationSurveyModal = ({
  isMobileView,
  setIsModalOpen,
  userSubscriptionDetails,
  setShowUpgradeYearlySubscriptionModal,
  userReportCount,
}: {
  isMobileView: boolean;
  setIsModalOpen;
  userSubscriptionDetails;
  setShowUpgradeYearlySubscriptionModal;
  userReportCount: number | null;
}) => {
  const [cancellationStep, setCancellationStep] = useState<0 | 1 | 2>(0);
  const [exitSurveyOptionSelected, setExitSurveyOptionSelected] = useState<string | null>(null);
  const [customExitSurvey, setCustomExitSurvey] = useState<string>('');

  const [isSubscriptionCancelling, setIsSubscriptionCancelling] = useState<boolean>(false);

  const lostAccessBenefits = [
    `You’ll lose ${
      userReportCount && userReportCount > 0
        ? `access to ${userReportCount} Airbnb Revenue Reports and`
        : ''
    } access to unlimited Airbnb Revenue Reports creation.`,
    'You’ll lose access to unlimited access to Airbnb Market Intelligence.',
  ];

  const paymentFrequency = userSubscriptionDetails['payment_frequency'];
  const subscriptionPlan = userSubscriptionDetails['subscription_plan'];

  if (subscriptionPlan == 'scale') {
    lostAccessBenefits.push(
      'You’ll lose access to unlimited access to listing-level monthly historical performance data.'
    );
  }

  const exitSurvey = [
    'I don’t need it anymore.',
    'Too expensive.',
    'Low quality.',
    'Switching to another service.',
    'Missing features.',
    'Others',
  ];

  const ExitSurveyUI = (
    <>
      <FormControl fullWidth>
        <RadioGroup
          value={exitSurveyOptionSelected}
          onChange={event => setExitSurveyOptionSelected(event.target.value)}
        >
          {exitSurvey.map(surveyText => (
            <FormControlLabel value={surveyText} control={<Radio />} label={surveyText} />
          ))}
        </RadioGroup>
      </FormControl>

      {exitSurveyOptionSelected === 'Others' && (
        <TextField
          variant="standard"
          value={customExitSurvey}
          sx={{ width: '280px' }}
          onChange={event => setCustomExitSurvey(event.target.value)}
        />
      )}
    </>
  );

  const LossOfBenifitsUI = (
    <>
      <Box sx={{ background: COLOR_PRIMARY, borderRadius: '12px', padding: '20px' }}>
        {lostAccessBenefits.map(lostAccess => (
          <Stack spacing={2}>
            <Stack
              spacing={2}
              direction="row"
              alignItems="center"
              sx={{ color: '#fff', fontSize: '18px', mb: '10px' }}
            >
              <ArrowForwardIosRoundedIcon sx={{ fontSize: '16px' }} />
              <Typography variant="body1">{lostAccess}</Typography>
            </Stack>
          </Stack>
        ))}
      </Box>
    </>
  );

  const userSignupCountryCode = useSelector((state: any) => state.dashboard.user.signupCountryCode);
  const userLocalCurrency = CountryCodeWithCurrency[userSignupCountryCode] || 'USD';
  const subscriptionPrice = formatCurrency(
    String(CurrencyWithSubscriptionPrice[userLocalCurrency][subscriptionPlan]['monthly'])
  );
  const currencySymbol = exchangeRates[userLocalCurrency].symbol;

  const SpecialOfferUI = (
    <Stack spacing={2} justifyContent={'center'} sx={{ mt: '-40px', px: '10%' }}>
      <img
        src={GiftImage}
        alt="Special Offer"
        style={{
          maxWidth: '150px',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      />
      <Typography
        variant="body1"
        textAlign={'center'}
        sx={{ fontSize: '24px', color: COLOR_TEXT_1 }}
      >
        We’d love to make Airbtics work for you!{' '}
        <strong style={{ fontWeight: 500 }}>Your next 6 months on us!</strong> Pay extra{' '}
        {currencySymbol}
        {subscriptionPrice} for additional 12 months.
      </Typography>
      <Typography variant="body2" sx={{ color: COLOR_TEXT_GREY }}>
        Enjoy powerful short-term rental insights and exclusive features available only to
        Expert/Scale members.
      </Typography>
    </Stack>
  );

  const getActionButtonUI = btnObj => {
    return (
      <Button
        variant="contained"
        onClick={btnObj['action']}
        disabled={btnObj.isDisabled ? true : false}
        sx={{
          color: btnObj['color'],
          background: btnObj['background'],
          maxWidth: '300px',
          '&:hover': {
            background: btnObj['background'] + '88',
          },
        }}
      >
        {btnObj['title']}
      </Button>
    );
  };

  const handleExitSurveySubmission = async () => {
    const URL = 'https://fld2j6mes8.execute-api.us-east-2.amazonaws.com/default/updateUserDetails';
    const cancellationReason =
      exitSurveyOptionSelected === 'Others' ? customExitSurvey : exitSurveyOptionSelected;
    const cancellationReasonSaved = await callApi(
      URL,
      {
        queryParams: {
          cancellationReason: cancellationReason,
        },
      },
      true,
      false,
      '',
      'GET',
      true
    );
  };

  const handleUnsubscription = async () => {
    setIsSubscriptionCancelling(true);
    await handleExitSurveySubmission();
    const URL = 'https://4rbg8m6w65.execute-api.us-east-2.amazonaws.com/default/unsubscribe_v2';
    try {
      const response = await callApi(
        URL,
        {
          queryParams: {
            // isTest: true,
          },
        },
        true,
        false,
        '',
        'GET',
        true
      );

      if (response?.subscription_cancelled) {
        window.location.reload();
      } else {
        console.log('Unsubscription Failed!');
      }
    } catch (err) {
      console.log('Unsubscription failed: ', err);
    }
    setIsSubscriptionCancelling(false);
  };

  const cancellationFlows = [
    {
      title: 'Are you sure you want to cancel your subscription?',
      body: LossOfBenifitsUI,
      actionButtons: [
        {
          title: 'Continue Cancellation',
          action: () => {
            setCancellationStep(paymentFrequency === 'monthly' ? 1 : 2);
          },
          color: COLOR_TEXT_GREY,
          background: COLOR_GREY_800,
        },
        {
          title: `Keep Airbtics ${subscriptionPlan}`,
          action: () => {
            setIsModalOpen(false);
          },
          color: '#fff',
          background: COLOR_PRIMARY_2,
        },
      ],
    },
    {
      body: SpecialOfferUI,
      actionButtons: [
        {
          title: 'Continue Cancellation',
          action: () => {
            setCancellationStep(2);
          },
          color: COLOR_TEXT_GREY,
          background: COLOR_GREY_800,
        },
        {
          title: 'Claim It',
          action: () => {
            setShowUpgradeYearlySubscriptionModal(true);
            setIsModalOpen(false);
          },
          color: '#fff',
          background: COLOR_PRIMARY_2,
        },
      ],
      placeActionButtonCenter: true,
      footerText:
        'After the promotional period, you will return to your original price unless you choose to cancel.',
    },
    {
      title: 'Please let us know why do you want to cancel ?',
      body: ExitSurveyUI,
      actionButtons: [
        {
          title: 'Unsubscribe',
          action: () => {
            handleUnsubscription();
          },
          color: COLOR_TEXT_GREY,
          background: COLOR_GREY_800,
          isDisabled: exitSurveyOptionSelected ? false : true,
        },
        {
          title: `Keep Airbtics ${subscriptionPlan}`,
          action: () => {
            setIsModalOpen(false);
          },
          color: '#fff',
          background: COLOR_PRIMARY_2,
        },
      ],
    },
  ];

  return (
    <>
      <Modal open={true} onClose={() => setIsModalOpen(false)}>
        <Box
          sx={{
            width: isMobileView ? '100dvw' : '70dvw',
            height: '80dvh',
            background: '#fff',
            borderRadius: '8px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            outline: 'none',
            border: 'none',
          }}
        >
          <Card sx={{ height: '100%', overflow: 'auto' }}>
            <CardHeader
              action={
                <Box sx={{ display: 'inline-box' }}>
                  <IconButton onClick={() => setIsModalOpen(false)}>
                    <CloseRoundedIcon sx={{ color: COLOR_RED }} />
                  </IconButton>
                </Box>
              }
              title="Subscription Cancellation"
              sx={{
                paddingBottom: 0,
                '.MuiCardHeader-title': {
                  fontSize: '24px',
                  fontWeight: 500,
                  color: '#000',
                },
              }}
            />
            <Divider sx={{ my: '10px' }} />
            <CardContent>
              {cancellationFlows[cancellationStep]['title'] && (
                <Typography variant="h5" sx={{ color: COLOR_TEXT_1 }}>
                  {cancellationFlows[cancellationStep]['title']}
                </Typography>
              )}
              {cancellationFlows[cancellationStep]['body'] && (
                <Typography variant="body1" sx={{ mt: '30px' }}>
                  {cancellationFlows[cancellationStep]['body']}
                </Typography>
              )}
            </CardContent>
            <CardActions
              sx={{
                my: '20px',
                justifyContent: cancellationFlows[cancellationStep]['placeActionButtonCenter']
                  ? 'center'
                  : 'flex-end',
              }}
            >
              {cancellationFlows[cancellationStep]['actionButtons']?.map(btnObj =>
                getActionButtonUI(btnObj)
              )}
            </CardActions>
            {cancellationFlows[cancellationStep]['footerText'] && (
              <Typography
                variant="body2"
                textAlign={'center'}
                sx={{ mt: '10px', color: COLOR_TEXT_GREY }}
              >
                {cancellationFlows[cancellationStep]['footerText']}
              </Typography>
            )}
          </Card>
        </Box>
      </Modal>
      {isSubscriptionCancelling && <FullScreenLoader />}
    </>
  );
};

const ActiveSubscriptionCard = ({
  setIsModalOpen,
  userSubscriptionDetails,
  setShowUpgradeYearlySubscriptionModal,
}: {
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  userSubscriptionDetails: any;
  setShowUpgradeYearlySubscriptionModal;
}) => {
  const [deleteButtonAnchorEl, setDeleteButtonAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const userSignupCountryCode = useSelector((state: any) => state.dashboard.user.signupCountryCode);
  const userLocalCurrency = CountryCodeWithCurrency[userSignupCountryCode] || 'USD';
  const subscriptionPlan = userSubscriptionDetails['subscription_plan'];
  const nextPaymentDate = userSubscriptionDetails['next_payment_date'];
  const paymentFrequency = userSubscriptionDetails['payment_frequency'];
  const subscriptionStatus = userSubscriptionDetails['subscription_status'];
  const subscriptionEndDate = userSubscriptionDetails['subscription_end_date'];
  const subscriptionPrice = formatCurrency(
    String(CurrencyWithSubscriptionPrice[userLocalCurrency][subscriptionPlan][paymentFrequency])
  );
  const currencySymbol = exchangeRates[userLocalCurrency].symbol;

  const subHeaderPrefix = subscriptionStatus === 'active' ? 'Next billing' : subscriptionStatus;

  return (
    <>
      {/* Upgrade Plan Section */}
      <Typography variant="h6" sx={{ mb: '10px', color: COLOR_TEXT_GREY }}>
        Your Current Plan
      </Typography>

      <Card sx={{ background: '#eee', boxShadow: 'none' }}>
        <CardHeader
          action={
            subscriptionStatus === 'active' ? (
              <Box sx={{ display: 'inline-box' }}>
                <IconButton onClick={event => setDeleteButtonAnchorEl(event.currentTarget)}>
                  <MoreVertIcon />
                </IconButton>
              </Box>
            ) : paymentFrequency === 'monthly' ? (
              <IconButton
                onClick={() => {
                  setShowUpgradeYearlySubscriptionModal(true);
                  setIsModalOpen(false);
                }}
              >
                <RedeemIcon sx={{ color: COLOR_PRIMARY }} />
              </IconButton>
            ) : (
              <></>
            )
          }
          title={
            <>
              {subscriptionPlan} Plan{' '}
              <Chip
                variant="outlined"
                label={subscriptionStatus}
                sx={{
                  color: subscriptionStatus === 'active' ? COLOR_GREEN : COLOR_RED,
                  borderColor: subscriptionStatus === 'active' ? COLOR_GREEN : COLOR_RED,
                  textTransform: 'capitalize',
                }}
              />
            </>
          }
          subheader={`${subHeaderPrefix} on ${formatDate(
            subscriptionStatus === 'active' ? nextPaymentDate : subscriptionEndDate
          )}`}
          sx={{
            paddingBottom: 0,
            '.MuiCardHeader-title': {
              fontSize: '20px',
              fontWeight: 500,
              color: COLOR_PRIMARY,
              textTransform: 'capitalize',
            },
            '.MuiCardHeader-subheader': {
              color: '#000',
              marginTop: '8px',
              textTransform: 'capitalize',
            },
          }}
        />
        <CardContent>
          <Typography
            variant="body1"
            sx={{ fontSize: '20px', fontWeight: 500, color: '#000', pt: 0 }}
          >
            {currencySymbol}
            {subscriptionPrice}{' '}
            <span style={{ fontSize: '14px', color: COLOR_TEXT_3 }}>
              /{paymentFrequency === 'yearly' ? 'year' : 'month'}
            </span>
          </Typography>
        </CardContent>
      </Card>

      <Popover
        open={Boolean(deleteButtonAnchorEl)}
        anchorEl={deleteButtonAnchorEl}
        onClose={() => setDeleteButtonAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        disablePortal={false}
        sx={{ position: 'absolute' }}
      >
        <Box
          sx={{
            background: '#fff',
            padding: '20px',
            borderRadius: '4px',
          }}
        >
          <Button
            onClick={() => setIsModalOpen(true)}
            sx={{
              background: COLOR_RED,
              color: '#fff',
              '&:hover': {
                background: COLOR_RED + '88',
              },
            }}
          >
            UNSUBSCRIBE
          </Button>
        </Box>
      </Popover>

      {paymentFrequency === 'monthly' && (
        <>
          {/* Upgrade Plan Section */}
          <Typography variant="h6" sx={{ mt: '20px', color: COLOR_TEXT_GREY }}>
            Upgrade Your Plan
          </Typography>

          <Divider sx={{ mt: '10px', mb: '20px' }} />

          <Box
            sx={{
              position: 'relative',
              border: `2px solid ${COLOR_TEXT_4}`,
              borderRadius: '20px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              maxWidth: '500px',
              gap: '10px',
              mb: '40px',
              boxShadow: `8px 8px 0 2px ${COLOR_PRIMARY_2_100}`,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: '20px',
                fontWeight: 500,
                color: COLOR_TEXT_2,
              }}
            >
              🚀 Upgrade Yearly
            </Typography>
            <Typography variant="body1" sx={{ color: COLOR_TEXT_4 }}>
              We’d love to make Airbtics work for you!{' '}
              <strong
                style={{
                  padding: '2px 4px',
                  background: COLOR_SUCCESS + '30',
                  borderRadius: '2px',
                }}
              >
                Your next 6 months on us!
              </strong>{' '}
              Pay extra {currencySymbol}
              {subscriptionPrice} for additional 12 months.
            </Typography>
            <Button
              onClick={() => setShowUpgradeYearlySubscriptionModal(true)}
              endIcon={<KeyboardDoubleArrowRightRoundedIcon sx={{ fontSize: '22px' }} />}
              variant="contained"
              sx={{ width: '140px', color: '#fff', mt: '10px', fontSize: '18px' }}
            >
              Claim It!
            </Button>
          </Box>
        </>
      )}
    </>
  );
};

const LoadingCard = () => {
  return (
    <Card sx={{ border: 'none', boxShadow: 'none' }}>
      <CardContent
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}
      >
        <CircularProgress size={60} />
      </CardContent>
    </Card>
  );
};

const NoDataFound = () => {
  return <Typography variant="body1">You don't have any subscriptions.</Typography>;
};

const BillingTab = ({
  isMobileView,
  setShowUpgradeYearlySubscriptionModal,
}: {
  isMobileView: boolean;
  setShowUpgradeYearlySubscriptionModal;
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [userSubscriptionDetails, setUserSubscriptionDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const user: User = useSelector(state => (state as any).dashboard.user);

  const [userReportCount, setUserReportCount] = useState<number | null>(null);

  const getTotalReportCreated = async () => {
    const URL =
      'https://3iugob4wql.execute-api.us-east-2.amazonaws.com/prod/airbnbCalculatorReportCRUD';
    const data = await callApi(URL, {}, true, false, '', 'GET', true);
    console.log('Data: ', data.length);
    data !== 'Unauthenticated request' ? setUserReportCount(data.length) : setUserReportCount(0);
  };

  useEffect(() => {
    const URL =
      'https://w2g9kh7lf4.execute-api.us-east-2.amazonaws.com/default/fetchCustomerSubscriptionDetails';

    const getUserSubscriptionDetails = async () => {
      const userDetails = await callApi(
        URL,
        {
          queryParams: {
            // isTest: true,
          },
        },
        true,
        false,
        '',
        'GET',
        true
      );
      console.log(userDetails);
      userDetails && !userDetails.error && setUserSubscriptionDetails(userDetails);
      setIsLoading(false);
    };

    if (user.email !== 'Trial') {
      getUserSubscriptionDetails();
      getTotalReportCreated();
    }
  }, [user]);

  return (
    <>
      {/* <Grid item xs={12} sm={12} md={6} lg={6} xl={6}> */}
      <Grid item xs={12}>
        {isLoading ? (
          <LoadingCard />
        ) : !userSubscriptionDetails ? (
          <NoDataFound />
        ) : (
          <ActiveSubscriptionCard
            setIsModalOpen={setIsModalOpen}
            userSubscriptionDetails={userSubscriptionDetails}
            setShowUpgradeYearlySubscriptionModal={setShowUpgradeYearlySubscriptionModal}
          />
        )}
      </Grid>
      {isModalOpen && (
        <CancellationSurveyModal
          isMobileView={isMobileView}
          setIsModalOpen={setIsModalOpen}
          userSubscriptionDetails={userSubscriptionDetails}
          setShowUpgradeYearlySubscriptionModal={setShowUpgradeYearlySubscriptionModal}
          userReportCount={userReportCount}
        />
      )}
    </>
  );
};

export default BillingTab;
